import React, { useState } from 'react';
import { CreditCard, Zap, Receipt, Package, Loader2, Check, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { cn } from '@/lib/utils';

export function PowerPackModal({ open, onOpenChange }) {
  const { powerPackInfo, purchasePowerPack, bonusScans, bonusTransactions } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('stripe');

  const packs = powerPackInfo?.packs || [];

  const handlePurchase = async () => {
    if (!selectedPack) return;
    setLoading(true);
    try {
      await purchasePowerPack(selectedMethod, selectedPack.id);
    } finally {
      setLoading(false);
    }
  };

  // Auto-select popular pack or first pack
  React.useEffect(() => {
    if (packs.length > 0 && !selectedPack) {
      const popular = packs.find(p => p.popular) || packs[0];
      setSelectedPack(popular);
    }
  }, [packs, selectedPack]);

  if (!powerPackInfo || packs.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Power Packs - Best Value!
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current bonus info */}
          {(bonusScans > 0 || bonusTransactions > 0) && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <span className="text-sm text-emerald-700 dark:text-emerald-400">Current bonus</span>
              <div className="flex gap-3 text-sm">
                {bonusScans > 0 && <span className="font-bold text-emerald-600">{bonusScans} scans</span>}
                {bonusTransactions > 0 && <span className="font-bold text-emerald-600">{bonusTransactions} transactions</span>}
              </div>
            </div>
          )}

          {/* Power Pack Options */}
          <div className="grid gap-3">
            {packs.map((pack) => (
              <Card 
                key={pack.id}
                className={cn(
                  "cursor-pointer transition-all border-2",
                  selectedPack?.id === pack.id 
                    ? "border-amber-500 bg-amber-500/5" 
                    : "border-border hover:border-amber-500/50",
                  pack.popular && "relative"
                )}
                onClick={() => setSelectedPack(pack)}
              >
                {pack.popular && (
                  <Badge className="absolute -top-2 left-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px]">
                    <Star className="w-3 h-3 mr-1" /> Most Popular
                  </Badge>
                )}
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center",
                        selectedPack?.id === pack.id 
                          ? "bg-gradient-to-br from-amber-500 to-orange-500" 
                          : "bg-muted"
                      )}>
                        <Sparkles className={cn(
                          "w-6 h-6",
                          selectedPack?.id === pack.id ? "text-white" : "text-muted-foreground"
                        )} />
                      </div>
                      <div>
                        <h3 className="font-bold">{pack.name}</h3>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Zap className="w-3 h-3" /> {pack.scans} scans
                          </span>
                          <span>+</span>
                          <span className="flex items-center gap-1">
                            <Receipt className="w-3 h-3" /> {pack.transactions} txns
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold">${pack.price}</div>
                      <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-600">
                        Save {pack.savings}
                      </Badge>
                    </div>
                  </div>
                  {selectedPack?.id === pack.id && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Check className="w-3 h-3 text-emerald-500" />
                          <span>Never expires</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Check className="w-3 h-3 text-emerald-500" />
                          <span>Instant delivery</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Check className="w-3 h-3 text-emerald-500" />
                          <span>All AI features</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Check className="w-3 h-3 text-emerald-500" />
                          <span>Budgets & Split</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Payment Method Selection */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Payment Method</p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={selectedMethod === 'stripe' ? 'default' : 'outline'}
                className="h-10"
                onClick={() => setSelectedMethod('stripe')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Card
              </Button>
              <Button
                variant={selectedMethod === 'paypal' ? 'default' : 'outline'}
                className="h-10"
                onClick={() => setSelectedMethod('paypal')}
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .76-.654h6.317c2.072 0 3.598.369 4.573 1.108.936.71 1.367 1.837 1.283 3.361-.057 1.04-.306 2.088-.74 3.112a7.123 7.123 0 0 1-1.695 2.337c-.662.634-1.461 1.108-2.389 1.41-.858.28-1.864.42-3.008.42H7.872a.77.77 0 0 0-.76.654l-.93 5.23a.641.641 0 0 1-.633.54h-3.97a.641.641 0 0 0-.634.74l-.044.239z"/>
                </svg>
                PayPal
              </Button>
            </div>
          </div>

          {/* Purchase Button */}
          <Button 
            className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            onClick={handlePurchase}
            disabled={loading || !selectedPack}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : selectedPack ? (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Get {selectedPack.name} for ${selectedPack.price}
              </>
            ) : (
              'Select a pack'
            )}
          </Button>

          <p className="text-[10px] text-center text-muted-foreground">
            Secure payment powered by Stripe. Save {selectedPack?.savings || '25%'} compared to buying separately!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
